/**
 * Variant Detector - Detects product variant types
 */
class ShopifyVariantDetector {
  /**
   * @param {Product} product - The product object
   * @returns {boolean} True if has digital variants
   */
  hasDigitalVariants(product) {
    if (!product.variants) {
      return false;
    }

    const hasDigital = product.variants.some(
      variant =>
        variant.option1 && variant.option1.toLowerCase().includes('digital')
    );

    return hasDigital;
  }

  /**
   * @param {Product} product - The product object
   * @returns {boolean} True if has print variants
   */
  hasPrintVariants(product) {

    if (!product.variants) {
      return false;
    }

    const hasPrint =
      product.variants.some(
        variant =>
          variant.option1 && variant.option1.toLowerCase().includes('print')
      ) || Boolean(product.handle && product.handle.includes('print'));

    return hasPrint;
  }

  /**
   * @param {Product} product - The product object
   * @returns {boolean} True if has rental variants
   */
  hasRentalVariants(product) {
    if (!product.variants) return false;
    return (
      product.variants.some(
        variant =>
          variant.option2 &&
          (variant.option2.toLowerCase().includes('rent') ||
            variant.option2.toLowerCase().includes('rental'))
      ) || Boolean(product.handle && product.handle.includes('rental'))
    );
  }

  /**
   * @param {Product} product - The product object
   * @returns {boolean} True if has buy variants
   */
  hasBuyVariants(product) {
    if (!product.variants) return false;
    return (
      product.variants.some(
        variant =>
          variant.option2 && variant.option2.toLowerCase().includes('buy')
      ) || !this.hasRentalVariants(product)
    );
  }

  /**
   * Get all unique format options (option1 values) for a product
   * @param {Product} product - The product object
   * @returns {string[]} Array of unique format options
   */
  getUniqueFormats(product) {
    if (!product.variants || product.variants.length === 0) {
      return [];
    }

    const option1Formats = this._extractOption1Formats(product);

    // If option1 has generic values, try option2 instead
    if (this._hasOnlyGenericValues(option1Formats)) {
      const option2Formats = this._extractOption2Formats(product);
      if (option2Formats.length > 0) {
        return option2Formats;
      }
    }

    return option1Formats;
  }

  /**
   * Extract unique formats from option1
   * @private
   */
  _extractOption1Formats(product) {
    return [
      ...new Set(
        product.variants
          .filter(variant => variant.option1 && variant.option1.trim() !== '')
          .map(variant => variant.option1.trim())
      ),
    ];
  }

  /**
   * Extract unique formats from option2
   * @private
   */
  _extractOption2Formats(product) {
    return [
      ...new Set(
        product.variants
          .filter(variant => variant.option2 && variant.option2.trim() !== '')
          .map(variant => variant.option2.trim())
      ),
    ];
  }

  /**
   * Check if formats array contains only generic placeholder values
   * @private
   */
  _hasOnlyGenericValues(formats) {
    const genericValues = ['default', 'title', 'default title', 'variant'];
    return (
      formats.length === 1 && genericValues.includes(formats[0].toLowerCase())
    );
  }

  /**
   * Check if this is a simple product (only one format or no clear format structure)
   * @param {Product} product - The product object
   * @returns {boolean} True if this is a simple product
   */
  isSimpleProduct(product) {
    const formats = this.getUniqueFormats(product);

    if (formats.length === 0) return true;
    if (formats.length > 1) return false;

    // Single format - analyze if it should show format button
    return !this._shouldShowFormatButton(product, formats[0]);
  }

  /**
   * Determines if a single format should show a format button
   * @private
   */
  _shouldShowFormatButton(product, formatValue) {
    const option2Values = this._getUniqueOption2Values(product);

    if (option2Values.length === 0) return false;
    if (option2Values.length > 1) return true; // Multiple options = show format

    // Single option2 - check if it's different from format
    return this._areValuesDifferent(formatValue, option2Values[0]);
  }

  /**
   * Gets unique option2 values from product variants
   * @private
   */
  _getUniqueOption2Values(product) {
    return [
      ...new Set(
        (product.variants || [])
          .filter(variant => variant.option2 && variant.option2.trim() !== '')
          .map(variant => variant.option2?.trim())
          .filter(Boolean)
      ),
    ];
  }

  /**
   * Checks if two values are semantically different (no shared prefix)
   * @private
   */
  _areValuesDifferent(value1, value2) {
    const v1 = value1.toLowerCase();
    const v2 = value2.toLowerCase();
    return !v2.startsWith(v1) && !v1.startsWith(v2);
  }

  /**
   * Check if product has multiple formats (complex product)
   * @param {Product} product - The product object
   * @returns {boolean} True if has multiple formats
   */
  hasMultipleFormats(product) {
    return !this.isSimpleProduct(product);
  }
}

window.ProductSystem = window.ProductSystem || {};
window.ProductSystem.ShopifyVariantDetector = ShopifyVariantDetector;
